import { useGetDealerListQuery } from '@api/customer.api';
import { useAddTechnicalMessageMutation } from '@api/technicalMessage.api';
import { Dealer } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import {
  BlockContainer,
  Button,
  DocumentList,
  DropdownField,
  DropdownOption,
  SearchDealerAutocomplete,
  SearchDealerValue,
  SmartLink,
  Title,
} from '@components';
import { useAppSelector } from '@hooks';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Input,
  Radio,
  RadioGroup,
  TextField,
  styled,
} from '@mui/material';
import { getSpecialPage } from '@store/app/app.slice';
import { t } from '@translations';
import { formatBytes } from '@utils/formatBytes.utils';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

type TechnicalMessageCreateBlockProps = {
  title?: string;
};

type TechnicalMessageFormFields = {
  messageTitle?: string;
  messageText?: string;
  messageType?: MessageFormType;
  dealers?: Dealer[];
  attachments?: File[];
  group?:
    | 'individual'
    | 'honda'
    | 'cramer'
    | 'tielburger'
    | 'e-power'
    | 'miemmedue'
    | 'mammotion'
    | 'shizuokaseiki'
    | 'all';
};

type MessageFormType = 'critical' | 'important' | 'information';

const TextArea = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    padding: '0 !important',
    height: 'auto',
  },
}));

export const TechnicalMessageCreateBlock: React.FC<TechnicalMessageCreateBlockProps> = ({
  title,
}: TechnicalMessageCreateBlockProps) => {
  const { register, handleSubmit, control, setValue, watch } = useForm<TechnicalMessageFormFields>({
    defaultValues: {
      messageType: 'critical',
      group: 'individual',
      attachments: [],
    },
  });
  const [selectedDealers, setSelectedDealers] = useState<SearchDealerValue[]>([]);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [trigger, { isSuccess, isLoading }] = useAddTechnicalMessageMutation();
  const { currentData, isLoading: isDealersLoading } = useGetDealerListQuery({
    offSet: 0,
    limit: 1000,
    filters: [
      {
        filterName: 'SearchByName',
      },
    ],
    includeService: true,
  });
  const fetchedDealers = currentData?.data?.customersListFound;

  const handleSearchDealerChange = (_: React.SyntheticEvent, newValue: SearchDealerValue) => {
    if (Array.isArray(newValue)) {
      setSelectedDealers(newValue);
    } else if (newValue) {
      setSelectedDealers([newValue]);
    } else {
      setSelectedDealers([]);
    }
  };

  const options = useMemo<DropdownOption<MessageFormType>[]>(
    () => [
      { label: t(TextCodes.TechnicalMessage.Status.Critical), value: 'critical' },
      { label: t(TextCodes.TechnicalMessage.Status.Important), value: 'important' },
      { label: t(TextCodes.TechnicalMessage.Status.Information), value: 'information' },
    ],
    []
  );

  const onSubmit = (data: TechnicalMessageFormFields) => {
    const formData = new FormData();
    data.attachments?.forEach((file) => {
      formData.append(`Attachments`, file);
    });
    data.messageTitle && formData.append('MessageTitle', data.messageTitle);
    data.messageText && formData.append('MessageText', data.messageText);
    data.messageType && formData.append('MessageType', data.messageType);
    data.group && formData.append('Group', data.group);
    data.group === 'individual' &&
      formData.append('Dealers', selectedDealers.map((dealer) => (dealer as Dealer)?.id).join(','));

    trigger(formData);
  };

  const handleDropdownChange = (value?: MessageFormType) => {
    setValue('messageType', value);
  };

  const onDocumentRemove = (key: number) => {
    const newFiles = [...attachments];
    newFiles.splice(key, 1);
    setAttachments(newFiles);
    setValue('attachments', newFiles);
  };
  const beremaTechnicalMessagePage = useAppSelector(
    getSpecialPage('beremaTechnicalMessageListPage')
  );

  const technicalMessagePage = beremaTechnicalMessagePage;
  return (
    <BlockContainer>
      {isSuccess ? (
        <Box display='flex' justifyContent='center' flexDirection='column' alignItems='center'>
          <Title variant='h2'>
            {t(TextCodes.TechnicalMessage.Block.MessageSuccessfullyCreated)}
          </Title>
          <SmartLink href={technicalMessagePage}>{t(TextCodes.Base.Back)}</SmartLink>
        </Box>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Title variant='h2' sx={{ mb: 4 }}>
            {title}
          </Title>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mt: 3 }}>
                <DropdownField
                  options={options}
                  label={t(TextCodes.TechnicalMessage.Block.Type)}
                  value={options.find((option) => option.value === watch('messageType'))?.label}
                  getValue={(value) => handleDropdownChange(value as MessageFormType)}
                />
              </Box>
              <TextField
                fullWidth
                required
                label={t(TextCodes.TechnicalMessage.Block.Title)}
                sx={{ mt: 4 }}
                {...register('messageTitle', { required: true })}
              />
              <TextArea
                label={t(TextCodes.TechnicalMessage.Block.Text)}
                fullWidth
                multiline
                required
                rows={10}
                sx={{ mt: 4 }}
                {...register('messageText', { required: true })}
              />
              <Box sx={{ mt: 4 }}>
                <DocumentList
                  showAlert
                  documents={attachments.map((file) => ({
                    name: file.name,
                    fileSize: formatBytes(file.size),
                  }))}
                  onRemove={onDocumentRemove}
                />
              </Box>

              <Controller
                control={control}
                name='attachments'
                render={({ field }) => {
                  return (
                    <FormControl sx={{ mt: 5 }}>
                      <Box width='fit-content'>
                        <label htmlFor='attachments'>
                          <Button
                            component='span'
                            startIcon={<CloudUploadIcon />}
                            variant='secondary'
                            sx={{ mt: 2 }}
                          >
                            {t(TextCodes.TechnicalMessage.Block.UploadDocuments)}
                          </Button>
                        </label>
                        <Input
                          onChange={(event) => {
                            const target = event.target as HTMLInputElement;
                            if (target.files) {
                              const newFiles = Array.from(target.files);
                              const allFiles = [...attachments, ...newFiles];
                              setAttachments(allFiles);
                              field.onChange(allFiles);
                            }
                            target.value = '';
                          }}
                          type='file'
                          id='attachments'
                          inputProps={{ accept: '.pdf,.jpg,.jpeg,.png', multiple: true }}
                          sx={{ display: 'none' }}
                        />
                      </Box>
                    </FormControl>
                  );
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name='group'
                control={control}
                defaultValue='individual'
                render={({ field }) => (
                  <FormControl>
                    <FormLabel id='dealer-group-label'>
                      {t(TextCodes.TechnicalMessage.Block.Group)}
                    </FormLabel>
                    <RadioGroup {...field} row aria-labelledby='dealer-group-label'>
                      <FormControlLabel value='individual' control={<Radio />} label='Individual' />
                      <FormControlLabel value='honda' control={<Radio />} label='Honda' />
                      <FormControlLabel value='cramer' control={<Radio />} label='Cramer' />
                      <FormControlLabel value='tielburger' control={<Radio />} label='Tielburger' />
                      <FormControlLabel value='e-power' control={<Radio />} label='E-power' />
                      <FormControlLabel value='biemmedue' control={<Radio />} label='Biemmedue' />
                      <FormControlLabel value='mammotion' control={<Radio />} label='Mammotion' />
                      <FormControlLabel
                        value='shizuokaseiki'
                        control={<Radio />}
                        label='Shizuoka Seiki'
                      />
                      <FormControlLabel value='all' control={<Radio />} label='All' />
                    </RadioGroup>
                  </FormControl>
                )}
              />
              <Box mt={4}>
                <SearchDealerAutocomplete
                  required={watch('group') === 'individual' && selectedDealers.length === 0}
                  sx={{ width: '100%', mt: 3 }}
                  disabled={watch('group') !== 'individual' || isDealersLoading}
                  dealers={fetchedDealers || []}
                  handleAutocompleteChange={handleSearchDealerChange}
                  value={selectedDealers as SearchDealerValue}
                  isMultiple
                  busy={isDealersLoading}
                />
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ mt: 2 }} />
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant='primary' type='submit' busy={isLoading}>
              {t(TextCodes.TechnicalMessage.Block.Send)}
            </Button>
          </Box>
        </form>
      )}
    </BlockContainer>
  );
};
